import {  t  } from "@/client/libs/dummy-i18n";
import { FileArrowUpIcon, PlusIcon } from "@phosphor-icons/react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, ScrollArea } from "@stackmorph/ui";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { Helmet } from "react-helmet-async";

import { useResumes } from "@/client/services/resume";
import { useUser } from "@/client/services/user/user";
import { useDialog } from "@/client/stores/dialog";

import { GridView } from "./_layouts/grid";

export const ResumesPage = () => {
  const { user } = useUser();
  const { resumes } = useResumes();
  const { open: openImportDialog } = useDialog("import");
  const { open: openResumeDialog } = useDialog("resume");

  // Memoize resume statistics to avoid recalculation
  const resumeStats = useMemo(() => {
    const list = Array.isArray(resumes) ? resumes : [];
    const total = list.length;
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = list.filter(r => 
      new Date(r.updatedAt).getTime() > oneWeekAgo
    ).length;
    return { total, recent };
  }, [resumes]);

  // Calculate premium status and expiry
  const premiumInfo = useMemo(() => {
    if (!user?.isPremium) return null;

    const expiryDateStr = user.premiumExpiry;
    const expiryDate = expiryDateStr ? new Date(expiryDateStr) : null;
    
    if (!expiryDate) return { isPremium: true, daysLeft: null };

    const daysLeft = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return { isPremium: true, daysLeft, expiryDate };
  }, [user?.isPremium, user]);

  return (
    <>
      <Helmet>
        <title>
          {t`Resumes`} - {t`Stackmorph Resume Builder`}
        </title>
      </Helmet>

      <div className="flex h-full flex-col">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between border-b border-border/40 pb-6"
        >
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">{t`Resumes`}</h1>
            <p className="text-sm text-muted-foreground">
              {resumeStats.total === 0
                ? t`Create and manage your professional resumes`
                : t`${resumeStats.total} ${resumeStats.total === 1 ? "resume" : "resumes"} • ${resumeStats.recent} updated recently`}
            </p>
          </div>

          <div className="flex items-center gap-6">
            {/* Quick stats - minimal */}
            <div className="hidden items-center gap-6 sm:flex">
              <div className="text-right">
                <div className="text-2xl font-semibold tabular-nums">{resumeStats.total}</div>
                <div className="text-xs text-muted-foreground">{t`Total`}</div>
              </div>
              <div className="h-10 w-px bg-border/60" />
              <div className="text-right">
                <div className="text-2xl font-semibold tabular-nums">0</div>
                <div className="text-xs text-muted-foreground">{t`Views`}</div>
              </div>
              
              {/* Premium Status with Expiry */}
              {premiumInfo?.isPremium && (
                <>
                  <div className="h-10 w-px bg-border/60" />
                  <div className="text-right">
                    <div className="text-sm font-semibold text-green-600">{t`Premium`}</div>
                    <div className="text-xs text-muted-foreground">
                      {premiumInfo.daysLeft === null 
                        ? t`Active`
                        : t`${premiumInfo.daysLeft} days left`}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Creative Create Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 15,
                  }}
                >
                  <Button
                    size="lg"
                    className="gap-2 rounded-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 px-6 shadow-lg shadow-purple-500/30 transition-all hover:shadow-purple-500/50"
                  >
                    <PlusIcon size={20} weight="bold" className="text-white" />
                    <span className="font-semibold text-white">{t`Create`}</span>
                  </Button>
                </motion.div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="gap-3 py-3" onClick={() => { openResumeDialog("create"); }}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10">
                    <PlusIcon size={16} weight="bold" className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{t`New Resume`}</div>
                    <div className="text-xs text-muted-foreground">{t`Start from scratch`}</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem className="gap-3 py-3" onClick={() => { openImportDialog("create"); }}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-500/10">
                    <FileArrowUpIcon size={16} weight="bold" className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{t`Import Resume`}</div>
                    <div className="text-xs text-muted-foreground">{t`Upload JSON file`}</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.div>

        {/* Resume Grid */}
        <ScrollArea className="flex-1 mt-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <GridView />
          </motion.div>
        </ScrollArea>
      </div>
    </>
  );
};
